import { inject, Injectable } from '@angular/core';
import {
  CaseDownloadDocument,
  ExternalLinkDownloadDocument,
  ReferenceAssociationDownloadDocument,
  ReferenceDownloadDocument,
  TrademarkImageDownloadDocument,
} from '@model/download-action.model';
import { ExcelExportOptions, ExcelResult } from '@model/excel-download-request.model';
import { ColumnMapping } from '@model/excel-types.model';
import { concatMap, defaultIfEmpty, defer, EMPTY, from, lastValueFrom, Observable, takeUntil, tap } from 'rxjs';

import { LoggerService } from './logger.service';

/**
 * Service that writes data to Excel sheets.
 * Uses Office.js Excel API, compatible with both Excel Online and Excel Desktop.
 *
 * NEW APPROACH: Row-by-row Observable pattern
 * - writeCaseDocumentObservable(doc): Observable<void>
 * - Suitable for concatMap in components
 * - Type-safe with Excel.RequestContext
 * - Streams individual rows instead of batching
 */
@Injectable({
  providedIn: 'root',
})
export class ExcelWriterService {
  private logger = inject(LoggerService);

  // ===== HEADERS FOR EACH DOCUMENT TYPE =====

  private readonly CASE_DOCUMENT_HEADERS = [
    'Case',
    'DestCase',
    'Trifold',
    'DestTrifold',
    'Sub-Type',
    'Title',
    'Date',
    'Document Name',
    'Local Document',
    'Environment',
    'Status',
    'Keywords',
    'Note',
    'ID',
  ];

  private readonly REFERENCE_ASSOCIATION_HEADERS = [
    'Case',
    'Reference Name',
    'Included in IDS',
    'Acknowledged',
    'Source',
    'Source Date',
    'Cited Date',
    'Notes',
    'Environment',
    'Status',
    'ID',
  ];

  private readonly REFERENCE_HEADERS = [
    'Reference Type',
    'Reference Name',
    'Country Code',
    'Kind Code',
    'Patent Number',
    'Publication Number',
    'Publication Date',
    'Issue Date',
    'NPL Date',
    'City',
    'Publisher',
    'Applicant',
    'Foreign Document Number',
    'Volume',
    'Pages/Columns/Lines',
    'Name of Author',
    'Title of Article',
    'Title of Item',
    'Inventor',
    'Assignee',
    'Custom Citation',
    'Document Name',
    'Local Document',
    'Environment',
    'Status',
    'Keywords',
    'Reference Notes',
    'Document ID',
  ];

  private readonly EXTERNAL_LINK_HEADERS = ['Case', 'Environment', 'Status', 'Link'];

  private readonly TRADEMARK_IMAGE_HEADERS = ['Case', 'Environment', 'Document Name / Trademark Image', 'Local Document'];

  // ===== COLUMN MAPPINGS (Type-safe) =====

  private readonly CASE_DOCUMENT_MAPPING: ColumnMapping<CaseDownloadDocument> = {
    headers: this.CASE_DOCUMENT_HEADERS,
    fieldNames: [
      'case',
      'destCase',
      'trifold',
      'destTrifold',
      'type',
      'title',
      'date',
      'documentName',
      'localDocument',
      'environment',
      'status',
      'keywords',
      'note',
      'id',
    ],
  };

  private readonly REFERENCE_ASSOCIATION_MAPPING: ColumnMapping<ReferenceAssociationDownloadDocument> = {
    headers: this.REFERENCE_ASSOCIATION_HEADERS,
    fieldNames: [
      'case',
      'referenceName',
      'includedInIds',
      'acknowledged',
      'source',
      'sourceDate',
      'citedDate',
      'note',
      'environment',
      'status',
      'id',
    ],
  };

  private readonly REFERENCE_MAPPING: ColumnMapping<ReferenceDownloadDocument> = {
    headers: this.REFERENCE_HEADERS,
    fieldNames: [
      'type',
      'referenceName',
      'countryCode',
      'kindCode',
      'patentNumber',
      'pubNumber',
      'pubDate',
      'issueDate',
      'nplDate',
      'city',
      'publisher',
      'applicant',
      'foreignDocumentNumber',
      'volume',
      'pages',
      'nameOfAuthor',
      'titleOfArticle',
      'titleOfItem',
      'inventor',
      'assignee',
      'customCitation',
      'documentName',
      'localDocument',
      'environment',
      'status',
      'keywords',
      'note',
      'id',
    ],
  };

  private readonly EXTERNAL_LINK_MAPPING: ColumnMapping<ExternalLinkDownloadDocument> = {
    headers: this.EXTERNAL_LINK_HEADERS,
    fieldNames: ['case', 'environment', 'status', 'link'],
  };

  private readonly TRADEMARK_IMAGE_MAPPING: ColumnMapping<TrademarkImageDownloadDocument> = {
    headers: this.TRADEMARK_IMAGE_HEADERS,
    fieldNames: ['case', 'environment', 'documentName', 'localDocument'],
  };

  writeCaseDocument(sourceTransform$: Observable<CaseDownloadDocument>, options?: ExcelExportOptions): Observable<ExcelResult> {
    return this.write<CaseDownloadDocument>(sourceTransform$, this.CASE_DOCUMENT_MAPPING, options);
  }

  writeReferenceAssociation(
    sourceTransform$: Observable<ReferenceAssociationDownloadDocument>,
    options?: ExcelExportOptions
  ): Observable<ExcelResult> {
    return this.write<ReferenceAssociationDownloadDocument>(sourceTransform$, this.REFERENCE_ASSOCIATION_MAPPING, options);
  }

  writeReferenceDocument(sourceTransform$: Observable<ReferenceDownloadDocument>, options?: ExcelExportOptions): Observable<ExcelResult> {
    return this.write<ReferenceDownloadDocument>(sourceTransform$, this.REFERENCE_MAPPING, options);
  }

  writeExternalLinkDocument(
    sourceTransform$: Observable<ExternalLinkDownloadDocument>,
    options?: ExcelExportOptions
  ): Observable<ExcelResult> {
    return this.write<ExternalLinkDownloadDocument>(sourceTransform$, this.EXTERNAL_LINK_MAPPING, options);
  }

  writeTrademarkImageDocument(
    sourceTransform$: Observable<TrademarkImageDownloadDocument>,
    options?: ExcelExportOptions
  ): Observable<ExcelResult> {
    return this.write<TrademarkImageDownloadDocument>(sourceTransform$, this.TRADEMARK_IMAGE_MAPPING, options);
  }

  // ============================================================
  // PRIVATE: Internal methods
  // ============================================================

  /**
   * Reusable method to write any data type to Excel based on provided mapping.
   * Handles header writing, row-by-row data writing, and returns total rows processed.
   *
   * @param data$ Observable stream of data to write
   * @param mapping Column mapping defining headers and field names
   * @param syncBatchSize Number of rows to write before syncing with Excel (default: 50)
   * @returns Observable emitting ExcelResult with rows processed
   */
  private write<T>(
    data$: Observable<T>,
    mapping: ColumnMapping<T>,
    options?: ExcelExportOptions,
    syncBatchSize: number = 50
  ): Observable<ExcelResult> {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel application is not available');
    }

    return defer(() =>
      from(
        Excel.run(async (context: Excel.RequestContext) => {
          const sheet = context.workbook.worksheets.getActiveWorksheet();

          let currentRow = 0;
          let syncCounter = 0;
          let rowsProcessed = 0;
          let cancelled = false;

          options?.cancel$?.subscribe(() => {
            cancelled = true;
          });

          const numCols = mapping.headers.length;

          const headerRange = sheet.getRangeByIndexes(currentRow++, 0, 1, numCols);
          headerRange.values = [mapping.headers];
          headerRange.format.font.bold = true;
          headerRange.format.fill.color = '#D9E1F2';

          await context.sync();

          // streaming write (inside one Excel.run)

          await lastValueFrom(
            data$.pipe(
              takeUntil(options?.cancel$ ?? EMPTY),
              concatMap((data) => {
                if (cancelled) {
                  return EMPTY;
                }

                const rowData = mapping.fieldNames.map((field) => data[field] ?? '');

                const range = sheet.getRangeByIndexes(currentRow++, 0, 1, rowData.length);

                // Force each cell in this row to Text format
                range.numberFormat = [rowData.map(() => '@')];
                // write values
                range.values = [rowData];

                rowsProcessed++;
                syncCounter++;

                if (syncCounter % syncBatchSize === 0) {
                  return from(context.sync()).pipe(tap(() => options?.onProgress$?.next(rowsProcessed)));
                }

                return EMPTY;
              }),
              defaultIfEmpty(null)
            )
          );

          //final flush
          await context.sync();

          options?.onProgress$?.next(rowsProcessed);

          return { rowsProcessed, cancelled };
        })
      )
    );
  }
}
